// File: frontend/src/components/common/UpdateAvailableBanner.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSyncAlt, FaTimes } from "react-icons/fa";
import { subscribeToUpdates, applyUpdate } from "../../serviceWorkerRegistration";
import "./UpdateAvailableBanner.css";

/**
 * Site-wide "a new version is available" strip (Ticket 4).
 *
 * Renders nothing until the service worker reports that a newer build has
 * finished installing and is waiting to take over. Clicking refresh activates
 * the waiting worker and reloads the page into the new build.
 *
 * The banner is only an accelerator: a user who ignores or dismisses it still
 * gets the new version on their next visit, because the waiting worker takes
 * over automatically once every tab for the site has been closed.
 */
export const UpdateAvailableBanner: React.FC = () => {
  const { t } = useTranslation();
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // subscribeToUpdates fires immediately with the current state, so an
    // update detected before this component mounted is not missed.
    const unsubscribe = subscribeToUpdates((available) => {
      setIsUpdateAvailable(available);
      if (available) {
        // A newer build than the one that was dismissed — show the strip again.
        setIsDismissed(false);
      }
    });
    return unsubscribe;
  }, []);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    applyUpdate();
  }, []);

  if (!isUpdateAvailable || isDismissed) return null;

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <FaSyncAlt className={`update-banner__icon${isRefreshing ? " update-banner__icon--spinning" : ""}`} />
      <span className="update-banner__text">
        {t("app_update_available", "Yeni sürüm mevcut.")}
      </span>
      <button
        type="button"
        className="update-banner__refresh"
        onClick={handleRefresh}
        disabled={isRefreshing}
      >
        {t("app_update_refresh", "Yenile")}
      </button>
      <button
        type="button"
        className="update-banner__dismiss"
        onClick={() => setIsDismissed(true)}
        aria-label={t("app_update_dismiss", "Kapat")}
        title={t("app_update_dismiss", "Kapat")}
      >
        <FaTimes />
      </button>
    </div>
  );
};